import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { loadContractPreferenceServer, saveContractdPreferenceServer } from "src/docuclipper/api";

interface ViewState {
    view: string | null;
    loading: boolean;
    error: string | null;
}

// Seed from localStorage so the sidebar paints in the correct view on first
// render, before loadViewPreference() round-trips to the server. Without this
// the sidebar flashes standard → tree on every page load for tree users.
const VIEW_CACHE_KEY = "uiView";
const readCachedView = (): string | null => {
    try {
        const v = localStorage.getItem(VIEW_CACHE_KEY);
        return v ? v : null;
    } catch {
        return null;
    }
};
const writeCachedView = (v: string | null) => {
    try {
        if (v) {
            localStorage.setItem(VIEW_CACHE_KEY, v);
        } else {
            localStorage.removeItem(VIEW_CACHE_KEY);
        }
    } catch {
        // ignore quota / privacy-mode failures
    }
};

const initialState: ViewState = {
    view: readCachedView(),
    loading: false,
    error: null,
};

const slice = createSlice({
    name: "View",
    initialState,
    reducers: {
        setView(state, action: PayloadAction<string | null>) {
            state.view = action.payload;
            writeCachedView(action.payload);
        },
        load(state) {
            state.loading = true;
            state.error = null;
        },
        setError(state, action: PayloadAction<string>) {
            state.loading = false;
            state.error = action.payload;
        },
        setSuccess(state) {
            state.loading = false;
            state.error = null;
        },
    },
});

export const { setView, load, setError, setSuccess } = slice.actions;

export const loadViewPreference = () => async (dispatch) => {
    try {
        dispatch(load());
        const response = await loadContractPreferenceServer({ key: "uiView" });
        dispatch(setView(response?.value || null));
        dispatch(setSuccess());
    } catch (error: any) {
        console.error("Failed to load view preference:", error);
        dispatch(setError(error.message || "Failed to load view preference"));
    }
};

export const saveViewPreference = (view: string | null) => async (dispatch) => {
    try {
        dispatch(load());
        await saveContractdPreferenceServer({
            key: "uiView",
            value: view || "",
        });
        dispatch(setView(view));
        dispatch(setSuccess());
    } catch (error: any) {
        console.error("Failed to save view preference:", error);
        dispatch(setError(error.message || "Failed to save view preference"));
    }
};

export default slice.reducer; 